import type { Color, Layer, PickingInfo } from "@deck.gl/core";
import { FillStyleExtension } from "@deck.gl/extensions";
import { GeoJsonLayer, IconLayer } from "@deck.gl/layers";
import centerOfMass from "@turf/center-of-mass";

import { riskPictureThumbBadgeUrl } from "./risk-picture-badge";
import {
  CHANCE_STRIPE_MASK_ATLAS,
  CHANCE_STRIPE_TILE_PX,
  chanceBaseColor,
  chanceStripeColor,
  RISK_PICTURE_NEUTRAL_COLOR,
  riskPictureLevelColor,
} from "./risk-picture-colors";
import type { RiskPictureItem } from "./risk-picture-model";
import { riskPicturePhenomenonIconUrl } from "./risk-picture-phenomena";
import { polygonOpacityTransition } from "./risk-picture-polygon-fade";

const stripeFill = new FillStyleExtension({ pattern: true });

/**
 * Meters per pattern texel. Deck.gl has no pixel width, only a size on the
 * ground, so this is just "1 texel = 1 CSS pixel" at the current zoom.
 * The stripe width itself is `CHANCE_STRIPE_WIDTH_PX`.
 */
export const chanceStripeWorldScale = (zoom: number | undefined): number =>
  40_000_000 / (512 * 2 ** (zoom ?? 6));

const LEVEL_RANK = { yellow: 1, orange: 2, red: 3 } as const;

/**
 * Opacity multiplier for the other polygons while one warning is hover-focused.
 * 0 hides them (matches the hidden markers); at 0 they also stop being pickable.
 */
const FOCUS_DIMMED_OPACITY = 0;

/** Higher is painted above. Warning code only; a part with no code is lowest. */
export const riskPictureStackRank = (item: Pick<RiskPictureItem, "level">): number =>
  item.level ? LEVEL_RANK[item.level] : 0;

/**
 * Map cards. 1–4 follow the code; 5 is the open card.
 * The filter panel sits at 6 so a card never covers it.
 */
export const riskPictureMarkerZIndex = (
  item: Pick<RiskPictureItem, "level">,
  open: boolean,
): number => (open ? 5 : riskPictureStackRank(item) + 1);

const byStackRank = (a: RiskPictureItem, b: RiskPictureItem): number =>
  riskPictureStackRank(a) - riskPictureStackRank(b);

/** Area center where a map marker or card sits. */
export const riskPictureItemPosition = (item: RiskPictureItem): [number, number] => {
  const [lng, lat] = centerOfMass(item.area).geometry.coordinates;
  return [lng, lat];
};

const fillColor = (item: RiskPictureItem): Color =>
  item.level ? riskPictureLevelColor(item.level) : RISK_PICTURE_NEUTRAL_COLOR;

export type RiskPicturePick = (item: RiskPictureItem, x: number, y: number) => void;

const pick = (item: RiskPictureItem, onPick?: RiskPicturePick) => (info: PickingInfo) => {
  if (!onPick || info.x === undefined || info.y === undefined) return;
  onPick(item, info.x, info.y);
};

const polygonLayers = (
  item: RiskPictureItem,
  idPrefix: string,
  onPick: RiskPicturePick | undefined,
  scale: number,
  /** Set on the big map. Absent for the thumbnails, which should not fade. */
  fade: { opacity: number; leaving?: boolean } | undefined,
): Layer[] => {
  const opacityTransition = polygonOpacityTransition(fade?.opacity, fade?.leaving);
  // A polygon at 0 is mid enter or leave. Clicks belong to the ones that are staying.
  const pickable = Boolean(onPick) && fade?.opacity !== 0;
  const shared = {
    opacity: fade?.opacity ?? 1,
    transitions: opacityTransition ? { opacity: opacityTransition } : undefined,
    pickable,
    onClick: pick(item, onPick),
  };

  if (item.kind !== "chance" || !item.level) {
    return [
      new GeoJsonLayer({
        id: `${idPrefix}-${item.id}`,
        slot: "middle",
        data: item.area,
        getFillColor: fillColor(item),
        getLineColor: fillColor(item),
        lineWidthMinPixels: 1,
        // Same solid fill as the table bar. A lower opacity tints it with the map.
        ...shared,
      }),
    ];
  }

  const base = chanceBaseColor(item.level);
  return [
    new GeoJsonLayer({
      id: `${idPrefix}-${item.id}`,
      slot: "middle",
      data: item.area,
      getFillColor: base,
      getLineColor: base,
      lineWidthMinPixels: 1,
      ...shared,
    }),
    new GeoJsonLayer({
      id: `${idPrefix}-${item.id}-stripes`,
      slot: "middle",
      data: item.area,
      filled: true,
      stroked: false,
      extensions: [stripeFill],
      fillPatternMask: true,
      fillPatternAtlas: CHANCE_STRIPE_MASK_ATLAS,
      fillPatternMapping: {
        lines: {
          x: 0,
          y: 0,
          width: CHANCE_STRIPE_TILE_PX,
          height: CHANCE_STRIPE_TILE_PX,
        },
      },
      getFillPattern: () => "lines",
      getFillPatternScale: scale,
      getFillColor: chanceStripeColor(item.level),
      opacity: shared.opacity,
      transitions: shared.transitions,
      pickable: false,
    }),
  ];
};

export const riskPictureLayers = (
  items: RiskPictureItem[],
  idPrefix: string,
  onPick?: RiskPicturePick,
  zoom?: number,
  /** Off for the big map, which draws HTML cards instead of icons. */
  withMarkers = true,
  /** Per item, only for the big map. Missing ids stay fully visible and do not fade. */
  fadeById?: ReadonlyMap<string, { opacity: number; leaving?: boolean }>,
  /** Hover focus dims the other polygons without interrupting enter/leave fades. */
  focusedItemId?: string,
): Layer[] => {
  const scale = chanceStripeWorldScale(zoom);
  const hasFocus = items.some(
    (item) => item.id === focusedItemId && !fadeById?.get(item.id)?.leaving,
  );
  const polygons = [...items]
    // Hover focus wins over severity; keep each chance base and stripe pair together.
    .sort(
      (a, b) =>
        (hasFocus ? Number(a.id === focusedItemId) - Number(b.id === focusedItemId) : 0) ||
        byStackRank(a, b),
    )
    .flatMap((item) => {
      const fade = fadeById?.get(item.id);
      const dimmed = hasFocus && item.id !== focusedItemId;
      return polygonLayers(
        item,
        idPrefix,
        onPick,
        scale,
        // `leaving` keeps the opacity transition at 0, so hiding fades instead of snapping.
        // A polygon still entering (opacity 0) has nothing to fade out from.
        dimmed
          ? {
              ...fade,
              opacity: (fade?.opacity ?? 1) * FOCUS_DIMMED_OPACITY,
              leaving: fade?.leaving || fade?.opacity !== 0,
            }
          : fade,
      );
    });

  if (!withMarkers) return polygons;

  // Draw each symbol right after its badge: same-code warnings can overlap on thumbnails.
  // Stacking all badges before all symbols leaves symbols outside the topmost badge.
  const markers = [...items].sort(byStackRank).flatMap((item) => {
    const data = [item];
    return [
      new IconLayer<RiskPictureItem>({
        id: `${idPrefix}-marker-badge-${item.id}`,
        data,
        getPosition: riskPictureItemPosition,
        getIcon: (item) => ({
          url: riskPictureThumbBadgeUrl(item.kind, item.level),
          width: 64,
          height: 64,
          anchorX: 32,
          anchorY: 32,
        }),
        getSize: 24,
        pickable: false,
      }),
      new IconLayer<RiskPictureItem>({
        id: `${idPrefix}-marker-icon-${item.id}`,
        data,
        getPosition: riskPictureItemPosition,
        getIcon: (item) => ({
          url: riskPicturePhenomenonIconUrl(item.phenomenon),
          width: 64,
          height: 64,
          anchorX: 32,
          anchorY: 32,
        }),
        // Keep the phenomenon symbol inset like the full-map badge (32px in a 40px square).
        getSize: 19.2,
        pickable: Boolean(onPick),
        onClick: (info) => {
          if (!info.object) return;
          pick(info.object, onPick)(info);
        },
      }),
    ];
  });

  return [...polygons, ...markers];
};
